import { useState } from 'react';
import {
  ChevronDown,
  ChevronRight,
  Copy,
  GripVertical,
  ListTree,
  Palette,
  Plus,
  Settings2,
  Trash2,
} from 'lucide-react';

import { cn } from '../../lib/utils';
import { Input } from '../../internal/primitives/input';
import { Label } from '../../internal/primitives/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../internal/primitives/select';
import { Separator } from '../../internal/primitives/separator';
import { Switch } from '../../internal/primitives/switch';
import { MapLegendSwatch } from './internal/MapLegendSwatch';
import type {
  MapWorkbenchAdvancedRuleConditionNode,
  MapWorkbenchAdvancedRuleDefinition,
  MapWorkbenchAdvancedRuleEditorPanelProps,
  MapWorkbenchAdvancedRuleGroupNode,
  MapWorkbenchAdvancedRuleLogicNode,
  MapWorkbenchAdvancedRuleCombinator,
  MapWorkbenchAdvancedRuleValidationMessage,
} from '../../contracts/map-workbench-advanced-rules';
import type { RuleFieldOption, RuleOperatorOption } from '../rule-editor/types';

const DEFAULT_RULE_COLOR = 'rgb(59 130 246)';

const generateId = (prefix: string) => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `${prefix}-${crypto.randomUUID()}`;
  }
  return `${prefix}-${Math.random().toString(36).slice(2, 10)}`;
}

const fieldLabelByType: Record<RuleFieldOption['value_type'], string> = {
  string: 'Text',
  number: 'Number',
  boolean: 'Boolean',
  date: 'Date',
}

const conditionInputClass =
  'h-8 rounded-lg border-border bg-muted/20 text-[11px] shadow-none transition-colors focus-visible:ring-1 focus-visible:ring-ring hover:bg-muted/30';

const ruleToneClass = (selected: boolean, readOnly?: boolean) =>
  cn(
    'rounded-xl border transition-all duration-200',
    selected 
      ? 'border-primary/40 bg-card shadow-lg ring-1 ring-primary/10' 
      : 'border-border/60 bg-card/40 shadow-sm hover:border-border/80 hover:bg-card/60',
    readOnly && 'opacity-95 cursor-default',
  );

const parseCommaSeparatedValues = (value: string) =>
  value
    .split(',')
    .map(entry => entry.trim())
    .filter(Boolean);

const formatConditionValue = (value: MapWorkbenchAdvancedRuleConditionNode['value']) => {
  if (value == null) {
    return '';
  }
  if (Array.isArray(value)) {
    return value.map(entry => String(entry)).join(', ');
  }
  if (typeof value === 'boolean') {
    return value ? 'true' : 'false';
  }
  return String(value);
}

const fieldById = (fieldId: string, fields: RuleFieldOption[]) => fields.find(entry => entry.id === fieldId);
const operatorById = (operatorId: string, operators: RuleOperatorOption[]) => operators.find(entry => entry.id === operatorId);

const fieldOperators = (fieldId: string, fields: RuleFieldOption[], operators: RuleOperatorOption[]) => {
  const field = fieldById(fieldId, fields);
  if (!field) {
    return operators;
  }
  const filtered = operators.filter(operator => operator.supported_value_types.includes(field.value_type));
  return filtered.length > 0 ? filtered : operators;
}

const valueInputKind = (field?: RuleFieldOption, operator?: RuleOperatorOption) => {
  if (!operator?.requires_value) {
    return 'none';
  }
  if (field?.value_type === 'boolean') {
    return 'boolean';
  }
  if (field?.value_type === 'number') {
    return 'number';
  }
  if (field?.value_type === 'date') {
    return 'date';
  }
  return 'text';
}

const summarizeCondition = (condition: MapWorkbenchAdvancedRuleConditionNode, fields: RuleFieldOption[], operators: RuleOperatorOption[]) => {
  const field = fieldById(condition.field, fields);
  const operator = operatorById(condition.operator, operators);
  const fieldLabel = field?.label ?? condition.field;
  const operatorLabel = operator?.label ?? condition.operator;
  const value = formatConditionValue(condition.value);
  return value ? `${fieldLabel} ${operatorLabel} ${value}` : `${fieldLabel} ${operatorLabel}`;
}

const summarizeLogic = (logic: MapWorkbenchAdvancedRuleLogicNode, fields: RuleFieldOption[], operators: RuleOperatorOption[]): string => {
  if (logic.kind === 'condition') {
    return summarizeCondition(logic, fields, operators);
  }
  if (logic.children.length === 0) {
    return 'No conditions yet';
  }
  const separator = logic.combinator === 'and' ? ' AND ' : ' OR ';
  const segments = logic.children.map(child => summarizeLogic(child, fields, operators));
  if (segments.length <= 2) {
    return segments.join(separator);
  }
  return `${segments.slice(0, 2).join(separator)} +${segments.length - 2} more`;
}

const createConditionNode = (field: string, operator: string): MapWorkbenchAdvancedRuleConditionNode => ({
  id: generateId('condition'),
  kind: 'condition',
  field,
  operator,
  value: null,
});

const createGroupNode = (combinator: MapWorkbenchAdvancedRuleCombinator = 'and'): MapWorkbenchAdvancedRuleGroupNode => ({
  id: generateId('group'),
  kind: 'group',
  combinator,
  children: [],
});

const ensureGroupNode = (logic: MapWorkbenchAdvancedRuleLogicNode): MapWorkbenchAdvancedRuleGroupNode =>
  logic.kind === 'group' ? logic : { ...createGroupNode(), children: [logic] };

const addChildNode = (
  logic: MapWorkbenchAdvancedRuleLogicNode,
  parentId: string,
  child: MapWorkbenchAdvancedRuleLogicNode,
): MapWorkbenchAdvancedRuleLogicNode => {
  if (logic.id === parentId && logic.kind === 'group') {
    return { ...logic, children: [...logic.children, child] };
  }
  if (logic.kind !== 'group') {
    return logic;
  }
  return {
    ...logic,
    children: logic.children.map(entry => addChildNode(entry, parentId, child)),
  };
}

const ruleValidationFor = (validation: MapWorkbenchAdvancedRuleValidationMessage[] | undefined, ruleId: string) =>
  (validation ?? []).filter(entry => entry.ruleId == null || entry.ruleId === ruleId);

const resolveRuleSwatchColor = (rule: MapWorkbenchAdvancedRuleDefinition) => {
  const paint = rule.paint ?? null;
  const colorProps =
    rule.representation === 'fill'
      ? ['fill-color']
      : rule.representation === 'line'
        ? ['line-color']
        : rule.representation === 'circle'
          ? ['circle-color']
          : rule.representation === 'fill-extrusion'
            ? ['fill-extrusion-color']
            : rule.representation === 'symbol'
              ? ['icon-color', 'text-color']
              : [];
  for (const prop of colorProps) {
    const value = paint?.[prop];
    if (typeof value === 'string' && value.trim().length > 0) {
      return value.trim();
    }
  }
  return rule.color || DEFAULT_RULE_COLOR;
}

const readRuleIconToken = (rule: MapWorkbenchAdvancedRuleDefinition) => {
  const value = rule.layout?.['icon-image'];
  return typeof value === 'string' && value.trim().length > 0 ? value.trim() : null;
}

function RuleValueEditor({
  field,
  operator,
  value,
  onChange,
  readOnly,
}: {
  field?: RuleFieldOption;
  operator?: RuleOperatorOption;
  value?: MapWorkbenchAdvancedRuleConditionNode['value'];
  onChange: (nextValue: MapWorkbenchAdvancedRuleConditionNode['value']) => void;
  readOnly?: boolean;
}) {
  const inputKind = valueInputKind(field, operator);
  if (inputKind === 'none') {
    return (
      <div className="flex h-8 items-center rounded-lg border border-dashed border-border bg-muted/10 px-3 text-[10px] font-medium text-muted-foreground">
        No value required.
      </div>
    );
  }

  if (inputKind === 'boolean') {
    return (
      <Select value={String(value ?? 'true')} onValueChange={next => onChange(next === 'true')} disabled={readOnly}>
        <SelectTrigger className={conditionInputClass}>
          <SelectValue placeholder="Value" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="true">True</SelectItem>
          <SelectItem value="false">False</SelectItem>
        </SelectContent>
      </Select>
    );
  }

  const useListInput = Array.isArray(value) || operator?.id === 'in' || operator?.id === 'not_in';
  const inputValue = formatConditionValue(value);
  return (
    <Input
      className={conditionInputClass}
      type={inputKind === 'number' ? 'number' : inputKind === 'date' ? 'date' : 'text'}
      value={inputValue}
      readOnly={readOnly}
      placeholder={inputKind === 'number' ? 'Number...' : inputKind === 'date' ? 'Date...' : 'Value...'}
      onChange={event => {
        const next = event.target.value;
        if (useListInput) {
          onChange(parseCommaSeparatedValues(next));
          return;
        }
        if (inputKind === 'number') {
          onChange(next === '' ? null : Number(next));
          return;
        }
        onChange(next);
      }}
    />
  );
}

function LogicNodeEditor({
  node,
  fields,
  operators,
  onChange,
  onDelete,
  readOnly,
  isRoot = false,
}: {
  node: MapWorkbenchAdvancedRuleLogicNode;
  fields: RuleFieldOption[];
  operators: RuleOperatorOption[];
  onChange: (next: MapWorkbenchAdvancedRuleLogicNode) => void;
  onDelete?: () => void;
  readOnly?: boolean;
  isRoot?: boolean;
}) {
  if (node.kind === 'condition') {
    const field = fieldById(node.field, fields);
    const availableOperators = fieldOperators(node.field, fields, operators);
    const operator = operatorById(node.operator, availableOperators) ?? operatorById(node.operator, operators);

    return (
      <div className="grid gap-2 py-1 transition-colors">
        <div className="flex flex-col gap-2 sm:grid sm:grid-cols-2">
          <Select
            value={node.field}
            onValueChange={nextField => {
              const nextFieldOption = fieldById(nextField, fields) ?? fields[0];
              const nextOperators = fieldOperators(nextField, fields, operators);
              const nextOperator = nextOperators[0]?.id ?? operators.find(entry => entry.supported_value_types.includes(nextFieldOption?.value_type ?? 'string'))?.id ?? node.operator;
              onChange({
                ...node,
                field: nextField,
                operator: nextOperator,
                value: nextFieldOption?.value_type === 'boolean' ? false : null,
              });
            }}
            disabled={readOnly}
          >
            <SelectTrigger className={conditionInputClass}>
              <SelectValue placeholder="Field" />
            </SelectTrigger>
            <SelectContent>
              {fields.map(fieldOption => (
                <SelectItem key={fieldOption.id} value={fieldOption.id}>
                  <div className="flex items-center justify-between gap-4 w-full">
                    <span className="truncate">{fieldOption.label}</span>
                    <span className="shrink-0 text-[9px] font-bold uppercase tracking-tighter text-muted-foreground opacity-60">{fieldLabelByType[fieldOption.value_type]}</span>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select
            value={node.operator}
            onValueChange={nextOperator => {
              const nextOperatorOption = operatorById(nextOperator, operators);
              onChange({
                ...node,
                operator: nextOperator,
                value: nextOperatorOption?.requires_value === false ? null : node.value,
              });
            }}
            disabled={readOnly}
          >
            <SelectTrigger className={conditionInputClass}>
              <SelectValue placeholder="Operator" />
            </SelectTrigger>
            <SelectContent>
              {availableOperators.map(operatorOption => (
                <SelectItem key={operatorOption.id} value={operatorOption.id}>
                  {operatorOption.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <div className="min-w-0 flex-1">
            <RuleValueEditor
              field={field}
              operator={operator}
              value={node.value}
              onChange={nextValue => onChange({ ...node, value: nextValue })}
              readOnly={readOnly}
            />
          </div>
          {onDelete ? (
            <button
              type="button"
              onClick={onDelete}
              disabled={readOnly}
              className="inline-flex h-8 items-center justify-center rounded-lg border border-border bg-background px-3 text-[10px] font-bold uppercase tracking-tight text-muted-foreground transition-all hover:bg-destructive/10 hover:text-destructive hover:border-destructive/20 disabled:cursor-not-allowed disabled:opacity-50 sm:w-20"
            >
              Remove
            </button>
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <div className={cn(
      "space-y-4",
      !isRoot && "relative ml-2 border-l-2 border-primary/20 pl-4 py-1"
    )}>
      <div className="flex items-center gap-2">
        <div className="inline-flex items-center gap-1.5 text-[9px] font-bold uppercase tracking-widest text-muted-foreground">
          <ListTree className="h-3 w-3" />
          Logical Group
        </div>
        <div className="ml-auto flex items-center gap-1.5">
          <button
            type="button"
            onClick={() => onChange({ ...node, combinator: node.combinator === 'and' ? 'or' : 'and' })}
            disabled={readOnly}
            className={cn(
              'inline-flex h-7 items-center justify-center rounded-lg border px-2.5 text-[10px] font-bold uppercase tracking-tight transition-all active:scale-[0.98]',
              node.combinator === 'and'
                ? 'border-primary/40 bg-primary/10 text-primary shadow-sm'
                : 'border-border bg-background text-muted-foreground hover:bg-muted/40 hover:text-foreground',
              readOnly && 'cursor-not-allowed opacity-60',
            )}
          >
            {node.combinator === 'and' ? 'Match All' : 'Match Any'}
          </button>
          {!isRoot && onDelete ? (
            <button
              type="button"
              onClick={onDelete}
              disabled={readOnly}
              className="inline-flex h-7 items-center justify-center rounded-lg border border-border bg-background px-2.5 text-[10px] font-bold uppercase tracking-tight text-muted-foreground transition-all hover:bg-destructive/10 hover:text-destructive hover:border-destructive/20 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Remove
            </button>
          ) : null}
        </div>
      </div>

      <div className="space-y-3">
        {node.children.length > 0 ? (
          node.children.map((child, index) => (
            <div key={child.id} className="space-y-3">
              {index > 0 ? (
                <div className="flex items-center gap-3 px-1">
                  <div className="h-[1px] flex-1 bg-border/40" />
                  <span className="text-[9px] font-black uppercase tracking-[0.2em] text-muted-foreground/40">{node.combinator === 'and' ? 'and' : 'or'}</span>
                  <div className="h-[1px] flex-1 bg-border/40" />
                </div>
              ) : null}
              <LogicNodeEditor
                node={child}
                fields={fields}
                operators={operators}
                readOnly={readOnly}
                onChange={nextChild =>
                  onChange({
                    ...node,
                    children: node.children.map(entry => (entry.id === child.id ? nextChild : entry)),
                  })
                }
                onDelete={
                  readOnly
                    ? undefined
                    : () =>
                        onChange({
                          ...node,
                          children: node.children.filter(entry => entry.id !== child.id),
                        })
                }
              />
            </div>
          ))
        ) : (
          <div className="rounded-lg border border-dashed border-border bg-muted/5 px-3 py-6 text-center">
            <p className="text-[10px] font-medium text-muted-foreground">Empty group. Add logic below.</p>
          </div>
        )}
      </div>

      <div className="flex items-center gap-2 pt-1">
        <button
          type="button"
          onClick={() => {
            const fallbackField = fields[0];
            if (!fallbackField) {
              return;
            }
            const fallbackOperator = fieldOperators(fallbackField.id, fields, operators)[0]?.id ?? operators.find(entry => entry.supported_value_types.includes(fallbackField.value_type))?.id ?? 'eq';
            onChange(addChildNode(node, node.id, createConditionNode(fallbackField.id, fallbackOperator)));
          }}
          disabled={readOnly || fields.length === 0}
          className="inline-flex h-7 items-center gap-1.5 rounded-lg border border-border bg-background px-2.5 text-[10px] font-bold uppercase tracking-tight text-foreground transition-all hover:bg-muted/40 hover:shadow-sm disabled:cursor-not-allowed disabled:opacity-50 active:scale-[0.98]"
        >
          <Plus className="h-3 w-3" />
          Condition
        </button>
        <button
          type="button"
          onClick={() => onChange(addChildNode(node, node.id, createGroupNode('and')))}
          disabled={readOnly}
          className="inline-flex h-7 items-center gap-1.5 rounded-lg border border-border bg-background px-2.5 text-[10px] font-bold uppercase tracking-tight text-foreground transition-all hover:bg-muted/40 hover:shadow-sm disabled:cursor-not-allowed disabled:opacity-50 active:scale-[0.98]"
        >
          <ListTree className="h-3 w-3" />
          Group
        </button>
      </div>
    </div>
  );
}

function RuleCard({
  rule,
  fields,
  operators,
  selected,
  validation,
  readOnly,
  onSelected,
  onChange,
  onEditStyle,
  onDuplicate,
  onDelete,
}: {
  rule: MapWorkbenchAdvancedRuleDefinition;
  fields: RuleFieldOption[];
  operators: RuleOperatorOption[];
  selected: boolean;
  validation: MapWorkbenchAdvancedRuleValidationMessage[];
  readOnly?: boolean;
  onSelected?: () => void;
  onChange: (nextRule: MapWorkbenchAdvancedRuleDefinition) => void;
  onEditStyle?: () => void;
  onDuplicate?: () => void;
  onDelete?: () => void;
}) {
  const group = ensureGroupNode(rule.logic);
  const expanded = selected || !onSelected;

  const updateRule = (patch: Partial<MapWorkbenchAdvancedRuleDefinition>) => onChange({ ...rule, ...patch });

  const ruleSummary = summarizeLogic(rule.logic, fields, operators);

  return (
    <section className={ruleToneClass(selected, readOnly)}>
      <button
        type="button"
        onClick={onSelected}
        disabled={!onSelected}
        className={cn(
          'flex w-full items-start gap-3 p-3.5 text-left transition-colors',
          onSelected ? 'cursor-pointer' : 'cursor-default',
          expanded && 'border-b border-border/40'
        )}
      >
        <div className="mt-1 flex flex-col items-center gap-1.5 shrink-0">
          <GripVertical className="h-4 w-4 text-muted-foreground/40" />
          <MapLegendSwatch
            className="ring-4 ring-background"
            label={rule.label || 'Rule'}
            color={resolveRuleSwatchColor(rule)}
            representation={rule.representation ?? null}
            iconToken={rule.iconToken ?? readRuleIconToken(rule)}
            iconPreviewUrl={rule.iconPreviewUrl ?? null}
            iconSdf={rule.iconSdf ?? false}
          />
        </div>
        
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="truncate text-xs font-bold tracking-tight text-foreground">{rule.label || 'Unnamed Rule'}</span>
            {selected && (
              <span className="h-1.5 w-1.5 rounded-full bg-primary animate-pulse" />
            )}
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-2 text-[10px] font-medium leading-none text-muted-foreground">
            <span className="truncate max-w-[180px]">{ruleSummary}</span>
            {validation.length > 0 && (
              <span className="text-destructive font-bold">· {validation.length} Alert{validation.length === 1 ? '' : 's'}</span>
            )}
          </div>
        </div>
        
        <div className="flex shrink-0 items-center gap-2">
          {onEditStyle && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onEditStyle();
              }}
              disabled={readOnly}
              className="flex h-8 items-center gap-1.5 rounded-lg border border-primary/20 bg-primary/5 px-2.5 text-[10px] font-bold uppercase tracking-tight text-primary transition-all hover:bg-primary/10 hover:border-primary/40 active:scale-[0.95] disabled:opacity-50"
            >
              <Palette className="h-3.5 w-3.5" />
              Customize Style
            </button>
          )}
          <div className="flex h-8 w-8 items-center justify-center rounded-lg border border-border bg-background/50 group-hover:bg-background">
            <Copy className={cn("h-3.5 w-3.5 text-muted-foreground/60 transition-transform", selected ? "rotate-0 scale-100" : "rotate-12 scale-90")} />
          </div>
        </div>
      </button>

      {expanded && (
        <div className="grid gap-5 p-4 animate-in fade-in slide-in-from-top-1 duration-200">
          <div className="grid gap-4 sm:grid-cols-[1fr_auto]">
            <div className="space-y-2">
              <Label className="text-[9px] font-black uppercase tracking-widest text-muted-foreground/80">Rule label</Label>
              <Input
                className={cn(conditionInputClass, "h-9 bg-background/50")}
                value={rule.label}
                readOnly={readOnly}
                onChange={event => updateRule({ label: event.target.value })}
                placeholder="Rule label..."
              />
            </div>

            <div className="flex items-end gap-1.5">
              <div className="flex h-9 items-center gap-2 rounded-lg border border-border bg-muted/20 px-3 transition-colors hover:bg-muted/30">
                <span className="text-[9px] font-black uppercase tracking-widest text-muted-foreground">Active</span>
                <Switch 
                  className="scale-75 origin-right" 
                  checked={rule.enabled} 
                  onCheckedChange={checked => updateRule({ enabled: checked })} 
                  disabled={readOnly} 
                />
              </div>
              <div className="flex items-center gap-1.5">
                {onDuplicate && (
                  <button
                    type="button"
                    onClick={onDuplicate}
                    disabled={readOnly}
                    title="Duplicate Rule"
                    className="flex h-9 w-9 items-center justify-center rounded-lg border border-border bg-background transition-all hover:bg-muted/40 hover:shadow-sm active:scale-[0.95]"
                  >
                    <Copy className="h-3.5 w-3.5 text-foreground" />
                  </button>
                )}
                {onDelete && (
                  <button
                    type="button"
                    onClick={onDelete}
                    disabled={readOnly}
                    title="Delete Rule"
                    className="flex h-9 w-9 items-center justify-center rounded-lg border border-border bg-background transition-all hover:bg-destructive/10 hover:text-destructive hover:border-destructive/20 active:scale-[0.95]"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className="space-y-3">
            <LogicNodeEditor
              node={group}
              fields={fields}
              operators={operators}
              readOnly={readOnly}
              isRoot={true}
              onChange={nextLogic => updateRule({ logic: nextLogic })}
            />
          </div>

          {validation.length > 0 && (
            <div className="space-y-1 rounded-lg border border-destructive/20 bg-destructive/5 px-3 py-2.5 shadow-inner">
              {validation.map(message => (
                <div
                  key={message.id}
                  className={cn(
                    'text-[10px] font-medium leading-relaxed',
                    message.level === 'error'
                      ? 'text-destructive'
                      : message.level === 'warning'
                        ? 'text-amber-700 dark:text-amber-300'
                        : 'text-muted-foreground',
                  )}
                >
                  {message.message}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </section>
  );
}

export function MapWorkbenchAdvancedRuleEditorPanel({
  rules,
  selectedRuleId,
  onSelectedRuleChange,
  onAddRule,
  onDuplicateRule,
  onDeleteRule,
  onRuleChange,
  onEditStyle,
  fields,
  operators,
  validation = [],
  legendHeading,
  legendLabel,
  onLegendHeadingChange,
  onLegendLabelChange,
  canEditLegend,
  footerMessage,
  canApply,
  applyDisabled,
  applyLabel = 'Apply changes',
  onApply,
  canSave,
  saveDisabled,
  saveLabel = 'Save changes',
  onSave,
  readOnly = false,
}: MapWorkbenchAdvancedRuleEditorPanelProps) {
  const [showLegendConfig, setShowLegendConfig] = useState(false);

  const panelMessages = validation.filter(entry => entry.ruleId == null);

  return (
    <div className="flex h-full min-h-0 flex-col bg-background text-foreground">
      <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4">
        <div className="space-y-4">
          {panelMessages.length > 0 ? (
            <section className="space-y-3">
              <div className="space-y-1 rounded-lg border border-border bg-muted/20 px-3 py-2">
                {panelMessages.map(message => (
                  <div
                    key={message.id}
                    className={cn(
                      'text-[11px]',
                      message.level === 'error'
                        ? 'text-destructive'
                        : message.level === 'warning'
                          ? 'text-amber-600 dark:text-amber-400'
                          : 'text-muted-foreground',
                    )}
                  >
                    {message.message}
                  </div>
                ))}
              </div>
            </section>
          ) : null}

          {panelMessages.length > 0 ? <Separator className="bg-border/40" /> : null}

          <section className="space-y-3">
            <div className="flex items-center justify-between gap-3 px-1">
              <div>
                <h2 className="text-sm font-semibold text-foreground">Rule definitions</h2>
                <p className="mt-0.5 text-[11px] text-muted-foreground">
                  Conditional highlighting for features.
                </p>
              </div>
              <button
                type="button"
                onClick={onAddRule}
                disabled={readOnly}
                className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-border bg-background px-3 text-[11px] font-semibold text-foreground transition-all hover:bg-muted/40 hover:shadow-sm disabled:cursor-not-allowed disabled:opacity-50 active:scale-[0.98]"
              >
                <Plus className="h-3.5 w-3.5" />
                Add rule
              </button>
            </div>

            {rules.length > 0 ? (
              <div className="space-y-2.5">
                {rules.map(rule => (
                  <RuleCard
                    key={rule.id}
                    rule={rule}
                    fields={fields}
                    operators={operators}
                    validation={ruleValidationFor(validation, rule.id)}
                    readOnly={readOnly}
                    selected={selectedRuleId === rule.id}
                    onSelected={onSelectedRuleChange ? () => onSelectedRuleChange(rule.id) : undefined}
                    onChange={nextRule => onRuleChange(rule.id, nextRule)}
                    onEditStyle={onEditStyle ? () => onEditStyle(rule.id) : undefined}
                    onDuplicate={readOnly ? undefined : () => onDuplicateRule(rule.id)}
                    onDelete={readOnly ? undefined : () => onDeleteRule(rule.id)}
                  />
                ))}
              </div>
            ) : (
              <div className="rounded-xl border border-dashed border-border bg-muted/5 px-4 py-6 text-center">
                <p className="text-xs text-muted-foreground">Add a rule to start building advanced styling behavior.</p>
              </div>
            )}
          </section>

          <Separator className="bg-border/40" />

          <section className="space-y-3 pb-2">
            <button
              type="button"
              onClick={() => setShowLegendConfig(!showLegendConfig)}
              className="flex w-full items-center justify-between gap-2 px-1 hover:text-primary transition-colors"
            >
              <div className="flex items-center gap-2">
                <Settings2 className="h-3.5 w-3.5 text-primary" />
                <h2 className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Legend display</h2>
              </div>
              {showLegendConfig ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
            </button>

            {showLegendConfig && (
              <div className="grid gap-3 animate-in fade-in slide-in-from-top-1 duration-200">
                <div className="space-y-2 px-1">
                  <Label className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Legend heading</Label>
                  <Input
                    className={cn(conditionInputClass, "h-10")}
                    value={legendHeading}
                    onChange={event => onLegendHeadingChange(event.target.value)}
                    readOnly={readOnly || !canEditLegend}
                    placeholder="Layer heading"
                  />
                </div>
                <div className="space-y-2 px-1">
                  <Label className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Legend label</Label>
                  <Input
                    className={cn(conditionInputClass, "h-10")}
                    value={legendLabel}
                    onChange={event => onLegendLabelChange(event.target.value)}
                    readOnly={readOnly || !canEditLegend}
                    placeholder="Summary label"
                  />
                </div>
              </div>
            )}
          </section>
        </div>
      </div>

      {(canApply || canSave || footerMessage) && (
        <div className="border-t border-border bg-card/50 p-3 shadow-sm backdrop-blur-sm">
          {footerMessage && (
            <div className="mb-3 text-[11px] leading-relaxed text-muted-foreground">
              {footerMessage}
            </div>
          )}
          <div className="flex flex-col gap-2">
            {canApply && (
              <button
                type="button"
                onClick={onApply}
                disabled={applyDisabled || readOnly}
                className="inline-flex h-9 w-full items-center justify-center rounded-lg bg-primary px-4 text-sm font-semibold text-primary-foreground transition-all hover:bg-primary/90 hover:shadow-md disabled:cursor-not-allowed disabled:opacity-50 active:scale-[0.98]"
              >
                {applyLabel}
              </button>
            )}
            {canSave && (
              <button
                type="button"
                onClick={onSave}
                disabled={saveDisabled || readOnly}
                className="inline-flex h-9 w-full items-center justify-center rounded-lg border border-border bg-background px-4 text-sm font-semibold text-foreground transition-all hover:bg-muted/40 hover:shadow-sm disabled:cursor-not-allowed disabled:opacity-50 active:scale-[0.98]"
              >
                {saveLabel}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
